import { formatCurrency, formatNumber } from '@angular/common';
import { inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Options, render } from 'ejs';
import { get, kebabCase, omit, pick, round, startCase } from 'lodash';
import { DateTime } from 'luxon';
import { marked } from 'marked';
import { IqEventBusBrokerService } from './iq-event-bus-broker.service';
import { z } from 'zod';

const TransformationRegisterSchema = z.object({
    key: z.string(),
    transformation: z.function(),
});

type TransformationRegister = z.infer<typeof TransformationRegisterSchema>;

const TransformationRegisterMapSchema = z.record(z.string(), z.function());
type TransformationRegisterMap = z.infer<
    typeof TransformationRegisterMapSchema
>;

/**
 * This service is used to transform data for the event bus. It leverages
 * lodash and luxon to provide a variety of transformations. Also,
 * the render function is used to render ejs templates with the data provided.
 */
@Injectable({
    providedIn: 'root',
})
export class IqEventBusTransformationService {
    private _sanitize: DomSanitizer = inject(DomSanitizer);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _transformations: Record<string, Function> = {};

    constructor() {
        this._bus.registerMap({
            addTransformation: {
                callback: (payload: TransformationRegister) => {
                    this.registerTransformation(payload);
                },
            },
            addTransformationMap: {
                callback: (payload: TransformationRegisterMap) => {
                    this.registerTransformationMap(payload);
                },
            },
        });
    }

    registerTransformationMap(
        payload: Record<string, (...args: unknown[]) => unknown>,
    ) {
        try {
            const check = TransformationRegisterMapSchema.parse(payload);
            Object.keys(check).forEach((key) => {
                this.registerTransformation({
                    key,
                    transformation: payload[key],
                });
            });
        } catch (e) {
            console.error(e);
        }
    }

    registerTransformation(payload: {
        key?: string;
        transformation?: (...args: unknown[]) => unknown;
    }) {
        try {
            TransformationRegisterSchema.parse(payload);

            this._transformations = {
                ...this._transformations,
                [payload.key]: payload.transformation,
            };
        } catch (e) {
            console.error(e);
        }
    }

    //#region Transformations
    /**
     * Splits the id by the delimeter and returns the part at the index
     *
     * Template:
     * ```html
     * <div>{{ splitId('1~2~3', 1) }}</div>
     * ```
     * Renders:
     * ```html
     * <div>2</div>
     * ```
     *
     * @param id - the id to split. (the delimeter is ~)
     * @param index - the index to get the part from the id
     * @returns the part of the id at the index
     */
    splitId(id: string, index: number, delimeter: string = '~'): string {
        if (!id) return '';

        const parts = id.split(delimeter);
        if (index < 0 || parts.length <= index) return '';

        return id.split(delimeter)[index];
    }

    /**
     * Creates an id by joining the parts with the delimeter.
     *
     * Template:
     * ```html
     * <div>{{ generateId(['1', '2', '3']) }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>1~2~3</div>
     * ```
     *
     * @param idParts - the parts to create the id
     * @param delimeter - the delimeter to use to join the parts
     * @returns an id with the parts joined by the delimeter
     */
    generateId(idParts: string[], delimeter: string = '~'): string {
        return idParts.join(delimeter);
    }
    /**
     * Takes in an array of strings and applies the lodash kebabCase function to each string,
     * Used mostly to convert strings for ids so they can be parsed easily for things like params
     *
     * Template:
     * ```html
     * <div>{{ generateId(kebabParts(['Hello World', 'Goodbye World'])) }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>hello-world~goodbye-world</div>
     * ```
     *
     *
     * @param parts - parts to transform to kebab case
     * @returns an array of strings that are in kebab case
     */
    kebabParts(parts: string[]): string[] {
        return parts.map((part: string) => kebabCase(part));
    }

    /**
     * Converts any string to a start case string leveraging lodash startCase
     *
     * Template:
     * ```html
     * <div>{{ startCase('hello world') }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>Hello World</div>
     * ```
     *
     * @param str - string to convert to start case
     * @returns a string that is start case
     */
    startCase(str: string) {
        return startCase(str);
    }

    /**
     * Converts any string to upper case
     *
     * Template:
     * ```html
     * <div>{{ upperCase('hello world') }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>HELLO WORLD</div>
     * ```
     *
     * @param str - string to convert to upper case
     * @returns a string that is upper case
     */
    upperCase(str: string) {
        return str.toUpperCase();
    }

    /**
     * Converts a string to lowercase.
     *
     * Template:
     * ```html
     * <div>{{ lowerCase('HELLO WORLD') }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>hello world</div>
     * ```
     *
     * @param str - The string to convert.
     * @returns The lowercase version of the string.
     */
    lowerCase(str: string) {
        return str?.toLowerCase();
    }

    /**
     * Rounds a number to a specified precision
     *
     * Template:
     * ```html
     * <div>{{ round(1.2345, 2) }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>1.23</div>
     * ```
     *
     * @param value - the number to round
     * @param precision - the precision to round to
     * @returns the rounded number
     */
    round(value: number, precision: number = 2) {
        return round(value, precision);
    }

    /**
     * Formats a number to a string. This is useful for adding commas to numbers.
     * Also, this will format the number based on the locale.
     *
     * Template:
     * ```html
     * <div>{{ formatNumber(12345) }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>12,345</div>
     * ```
     *
     * @param value - the number to format
     * @returns the formatted number
     */
    formatNumber(value: number, local: string = 'en-US'): string {
        return formatNumber(value, local);
    }

    /**
     * Gets the current date in the format specified
     *
     * Template:
     * ```html
     * <div>{{ todaysDate('MM/dd/yy') }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>12/31/21</div>
     * ```
     * Luxon formats: https://github.com/moment/luxon/blob/master/docs/formatting.md
     *
     * Cheat Sheet:
     * | Format     | Description                 | Example      |
     * | ---------- | --------------------------- | ------------ |
     * | `MM/dd/yy` | Used for keys               | 12/31/21     |
     * | `ccc`      | Abbreviated day of the week | Mon          |
     * | `cccc`     | Full day of the week        | Monday       |
     * | `DD`       | Full date                   | Dec 31, 2021 |

     *
     * @param format - the format to return the date in
     * @returns the current date in the specified format
     */
    todaysDate(format = 'MM/dd/yy'): string {
        return DateTime.now().toFormat(format);
    }

    /**
     * Formats a date string to a specified format
     *
     * Template:
     * ```html
     * <div>{{ formatDate('2021-12-31', 'MM/dd/yy') }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>12/31/21</div>
     * ```
     * @param date - the date to format
     * @param format - the format to use
     * @returns formatted string
     */
    formatDate(date: string, format = 'MM/dd/yy'): string {
        return DateTime.fromISO(date).toFormat(format);
    }

    /**
     * Gets the current date in the format MM/dd/yy
     *
     * For rates and other time based data, this is the format
     * used in the "date" property.
     *
     * Template:
     * ```html
     * <div>{{ todaysDateKey() }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>12/31/21</div>
     * ```
     *
     * @returns the current date in the format MM/dd/yy
     */
    todaysDateKey(): string {
        return this.todaysDate('MM/dd/yy');
    }

    /**
     * Gets the current date in ISO format.
     *
     * Used to create timestamps
     *
     * Template:
     * ```html
     * <div>{{ nowISO() }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>2021-12-31T00:00:00.000-05:00</div>
     * ```
     *
     * @returns the current date in ISO format
     */
    nowISO(): string {
        return DateTime.now().toISO();
    }

    /**
     * Formats the number to a currency string
     *
     * Template:
     * ```html
     * <div><%- currency(12345) %></div>
     * ```
     *
     * Renders:
     * ```html
     * <div>$12,345.00</div>
     * ```
     *
     *
     * @param value - the number to format
     * @param withCents - whether to include cents
     * @param locale - the locale to use
     * @param currency - the currency symbol
     * @param currencyCode - the currency code
     * @returns a formatted string
     */
    currency(
        value: number,
        withCents: boolean = true,
        locale: string = 'en-US',
        currency = '$',
        currencyCode = 'USD',
    ): string {
        if (isNaN(value)) return '$0.00';

        return withCents
            ? formatCurrency(value, locale, currency, currencyCode)
            : formatCurrency(value, locale, currency, currencyCode, '1.0-0');
    }

    /**
     * Renders html that has a material icon and an optional label. Works
     * with the custom tailwind classes to make the icon position correctly with the icon.
     *
     * Template:
     * ```html
     * <div>{{ mdIconLabel('check_box', 'Checked', true, { classes: ['text-dat-blue-500'] }) }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>
     *   <span
     *       data-uitype="md-icon"
     *       class="material-symbols-outlined iq-md-icon-label-icon iq-mat-icon-fill text-dat-blue-500"
     *       >check_box</span
     *   >
     *   <span class="iq-md-icon-label-text text-dat-blue-500">Checked</span>
     * </div>
     * ```
     */
    mdIconLabel(
        icon: string,
        label: string = '',
        filled: boolean = false,
        opts: { classes: string[] } = { classes: [] },
    ): string {
        const classes = opts.classes.join(' ');
        const filledClass = filled ? 'iq-mat-icon-fill' : '';
        return !!label.length
            ? `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${filledClass} ${classes}'>${icon || ''}</span><span class='iq-md-icon-label-text ${classes}'>${label}</span>`
            : `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${filledClass} ${classes}'>${icon || ''}</span>`;
    }

    /**
     * To allow us an easier way to create checkboxes with labels, this function will return a
     * label that has a checkbox icon and the label text. This way we can have things look like
     * checkboxes without having to use the actual input element.
     *
     * Template:
     * ```html
     * <div>{{ checkboxLabel('Checked', true, { classes: ['text-dat-blue-500'] }) }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>
     *  <span
     *     data-uitype="md-icon"
     *    class="material-symbols-outlined iq-md-icon-label-icon iq-mat-icon-fill text-dat-blue-500"
     * >check_box</span>
     * <span class="iq-md-icon-label-text text-dat-blue-500">Checked</span>
     * </div>
     * ```
     *
     * @param label - Label for the checkbox
     * @param icon - The icon to use for the checkbox
     * @param filled - Whether the checkbox is filled or not
     * @param opts.classes - The classes to apply to the checkbox
     * @param checked
     * @returns
     */
    checkboxLabel(
        label: string,
        checked: boolean,
        opts: { classes: string[] } = { classes: [] },
        icon?: string,
        filled?: boolean,
    ): string {
        return checked
            ? checkBoxLabelChecked(label, opts, icon, filled)
            : checkBoxLabelUnchecked(label, opts, icon, filled);
    }

    /**
     * Gets a value from an object using a path.
     *
     * This is helpful when you aren't sure if all the properties are defined
     *
     * Template:
     * ```html
     * <div>{{ get({ a: { b: { c: 1 } } }, 'a.b.c') }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>1</div>
     * ```
     *
     * @param obj - the object to get the value from
     * @param path - the path to the value
     * @returns the value at the path
     */
    get(obj: any, path, defaultValue: any = undefined) {
        return get(obj, path, defaultValue);
    }

    /**
     * Maps an array to html using a template
     *
     * You must use the `$` delimiter to access the data in the template
     *
     * Template:
     * ```html
     * <div>{{ mapArrayToHtml([{ name: 'John' }, { name: 'Jane' }], '<div><$= name $></div>') }}</div>
     * ```
     *
     * Renders:
     * ```html
     * <div>
     *  <div>John</div>
     * <div>Jane</div>
     * </div>
     * ```
     *
     * @param array - data to create html from
     * @param template - the template to render
     * @param data - the data to use for rendering
     * @returns string
     */
    mapArrayToHtml(
        array: any[] = [],
        template: string,
        data: any = {},
    ): string {
        return array
            .map((item, index) =>
                render(
                    template,
                    {
                        ...data,
                        ...this.getTransformations(template, data),
                        item,
                        index,
                    },
                    { delimiter: '$' },
                ),
            )
            .join('');
    }

    /**
     * This will convert any boolean to a string that can be used to help convert complex if statements to enums
     *
     * Template:
     * ```html
     * <div><%- boolToString({ a: true, b: false, c: false }, 'default') %></div>
     * ```
     *
     * Renders:
     * ```html
     * <div>a</div>
     * ```
     *
     * @param value - a record of strings to booleans that if the value is true, it will return the key
     * @param defaultString - the string to return if no value is true
     */
    boolToString(
        value: Record<string, boolean>,
        defaultString: string = '',
    ): string {
        return Object.keys(value).find((key) => value[key]) ?? defaultString;
    }

    /**
     * Converts an array to a string
     *
     * Template:
     * ```html
     * <!-- Note: you have to use <%- instead of <%= to render the html -->
     * <div><%- toArray([1, 2, 3]) %></div>
     * ```
     *
     * Renders:
     * ```html
     * <div>[1, 2, 3]</div>
     * ```
     *
     * @param arrayLike - the array to convert to a string
     * @returns the string version of the array
     */
    toArray(arrayLike: any[]): string {
        if (!Array.isArray(arrayLike)) return '[]';

        return JSON.stringify(arrayLike, null, 4);
    }

    /**
     * Removes properties from an object
     *
     * Template:
     * ```html
     * <div><%- omit({ a: 1, b: 2, c: 3 }, ['a', 'b']) %></div>
     * ```
     *
     * Renders:
     * ```html
     * <div>{ c: 3 }</div>
     * ```
     *
     * @param data - data to remove properties from
     * @param path - array of paths to remove
     * @returns data with those paths removed
     */
    omit(data: any, path: string[]) {
        return omit(data, path);
    }
    
    /**
     * Picks only the properties from an object
     * 
     * Template:
     * ```html
     * <div><%- pick({ a: 1, b: 2, c: 3 }, ['a', 'b']) %></div>
     * ```
     * 
     * Renders:
     * ```html
     * <div>{ a: 1, b: 2 }</div>
     * ```
     * @param data - data to pick values from 
     * @param path - array of paths to pick
     * @returns data with only the paths picked
     */
    pick(data: any, path: string[]) {
        return pick(data, path);
    }

    //#endregion

    /**
     * Maps all the service functions to a transformation object that can be passed into
     * ejs.
     *
     * @returns an object of all the available transformations and other function to be leveraged in templates
     */
    getTransformations(template: string = '', data: any = {}) {
        return {
            ...this._transformations,
            splitId: (id: string, index: number) => this.splitId(id, index),
            omit: (data: any, paths: string[]) => this.omit(data, paths),
            generateId: (idParts: string[]) => this.generateId(idParts),
            startCase: (str: string) => this.startCase(str),
            kebabParts: (parts: string[]) => this.kebabParts(parts),
            upperCase: (str: string) => this.upperCase(str),
            lowerCase: (str: string) => this.lowerCase(str),
            toArray: (arrayLike: any[]) => this.toArray(arrayLike),
            todaysDateKey: () => this.todaysDateKey(),
            nowISO: () => this.nowISO(),
            round: (value: number, precision: number = 2) =>
                this.round(value, precision),
            currency: (value: number, withCents: boolean = true) =>
                this.currency(value, withCents),
            get: (obj: any, path: string, defaultValue?: any) =>
                this.get(obj, path, defaultValue),
            formatNumber: (value: number) => this.formatNumber(value),
            formatDate: (date: string, format: string = 'MM/dd/yy') =>
                this.formatDate(date, format),
            mdIconLabel: (
                icon: string,
                label: string = '',
                filled: boolean = false,
                opts: { classes: string[] } = { classes: [] },
            ) => this.mdIconLabel(icon, label, filled, opts),
            checkboxLabel: (
                label: string,
                checked: boolean,
                opts: { classes: string[] } = { classes: [] },
                icon?: string,
                filled?: boolean,
            ) => this.checkboxLabel(label, checked, opts, icon, filled),
            boolToString: (value: Record<string, boolean>) =>
                this.boolToString(value),
            debug: (obj: any, title: string = '') => {
                return this.debug(obj, title, template, data);
            },
            mapArrayToHtml: (array: any[], template: string) =>
                this.mapArrayToHtml(array, template, data),
        };
    }

    /**
     * This is just to help us debug the transformations so we can understand what is going on.
     *
     * @param obj - object to debug This is the object that is being transformed
     * @param title - title of this debug group
     * @param template - the template being used
     * @param data - the data used for debugging
     * @returns the current object
     */
    debug(obj: any, title: string = '', template: string = '', data: any = {}) {
        console.groupCollapsed(!!title?.length ? title : 'DEBUGGING TEMPLATE');
        console.log('OBJECT', obj);
        console.log('DATA', data);
        console.log('TEMPLATE', template);
        console.groupEnd();

        return obj;
    }

    /**
     *
     * @param template - ejs template to set up the rendered data
     * @param data - the data to fill in with the template
     * @returns a rendered string with the data filled in
     */
    render(template: string, data: any, options: Options = {}): string {
        try {
            const md: string = render(
                template,
                {
                    ...data,
                    ...this.getTransformations(template, data),
                },
                {
                    ...options,
                    async: false,
                },
            );

            return marked(md);
        } catch (e) {
            console.error(e);
            return '';
        }
    }

    /**
     * Takes in a ejs template that is a json object and renders it with the data provided
     *
     * @param template json template to render
     * @param data the data to fill in with the template
     * @param options additional ejs compile options
     * @returns a rendered json object
     */
    renderJson(
        template: string,
        data: any,
        options: Options = {},
        errorDebugger?: (err: any, template: string, data: any) => void,
    ): any {
        try {
            const jsonString: string = render(
                template,
                {
                    ...data,
                    ...this.getTransformations(template, data),
                },
                {
                    ...options,
                    async: false,
                },
            )
                /**
                 * This is to convert the string "true" to the boolean true
                 */
                .replace(/"true"/g, 'true')
                /**
                 * This is to convert the string "false" to the boolean false
                 */
                .replace(/"false"/g, 'false')
                /**
                 * This is to convert an empty string to null so it is easier
                 * to show non existent values
                 */
                .replace(/\"\"/gm, 'null')
                /**
                 * This is to convert a positive float string to a floar
                 */
                .replace(/(\")([0-9]*\.[0-9]*)(\")/gm, '$2')
                /**
                 * This is to convert a negative float string to a float
                 */
                .replace(/(\")(\-[0-9]*\.[0-9]*)(\")/gm, '$2')
                /**
                 * This is to convert a positive integer string to an integer
                 *
                 * !NOTE: Numbers with a leading zero will not be converted
                 */
                .replace(/(\")([1-9]\d*)(\")/gm, '$2')
                /**
                 * Replaces a 0 string with a number 0
                 */
                .replace(/\"0\"/gm, '0')
                /**
                 * This is to convert a negative integer string to an integer
                 */
                .replace(/(\")(\-[1-9]\d*)(\")/gm, '$2')
                /**
                 * This is to conver an opening array string to an array
                 */
                .replace(/\"\[\n/gm, '[')
                /**
                 * This is to convert a closing array string to an array
                 */
                .replace(/\n\]\"/gm, ']')
                /**
                 * This is to convert an enoty array string to an array
                 */
                .replace(/\"\[\]\"/gm, '[]')
                /**
                 * This is to convert an opening object string to an object
                 */
                .replace(/\"\{/gm, '{')
                /**
                 * This is to convert a closing object string to an object
                 */
                .replace(/\}\"/gm, '}')
                .trim();

            const currentInfo = JSON.parse(jsonString);
            return currentInfo;
        } catch (e) {
            if (errorDebugger) errorDebugger(e, template, data);
            console.error(e);
            return {};
        }
    }
}

function checkBoxLabelChecked(
    label: string,
    opts: { classes: string[] } = { classes: [] },
    icon: string = 'check_box',
    filled: boolean = true,
): string {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';

    return `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon text-dat-blue-500 ${classes} ${filledClass}'>${icon}</span> <span class='iq-md-icon-label-text'>${label}</span>`;
}

function checkBoxLabelUnchecked(
    label: string,
    opts: { classes: string[] } = { classes: [] },
    icon: string = 'check_box_outline_blank',
    filled: boolean = false,
): string {
    const classes = opts.classes.join(' ');
    const filledClass = filled ? 'iq-mat-icon-fill' : '';

    return `<span data-uitype='md-icon' class='material-symbols-outlined iq-md-icon-label-icon ${classes} ${filledClass}'>${icon}</span> <span class='iq-md-icon-label-text'>${label}</span>`;
}
